/**
 * React Joyride step definitions for dashboard and job-details product tours.
 *
 * - `tourStepKey`: stable id for branching in `useProductTourJoyride` (see `productOnboarding.constants`).
 * - `data.allowTooltipDrag`: optional draggable tooltip (Meta CV beats); see `DraggableJoyrideTooltip`.
 */
import { createElement, Fragment } from 'react';
import type { Step } from 'react-joyride';
import type { ProductTourStepData } from './productOnboarding.types';
import { firstJobDetailsTourAnchor, firstVisibleTourTarget } from './productOnboarding.utils';

export type TourStep = Step & { spotlightClicks?: boolean; tourStepKey?: string };

/** Optional beats (e.g. Meta CV) are excluded from the progress denominator on the Next button. */
const JOB_DETAILS_OPTIONAL_PROGRESS_KEYS = new Set(['meta-cv-from-table', 'meta-cv-from-chart']);

/**
 * The Meta CV shell is almost full-viewport; using it as the Joyride *target* leaves no margin for a
 * wide tooltip (any side clips). Anchor the floater to the close control instead, and keep the
 * spotlight on the full panel via `spotlightTarget`.
 */
const metaCvTooltipStyles = {
  tooltip: {
    maxHeight: 'min(85dvh, 520px)',
  },
  tooltipContent: {
    maxHeight: 'min(38dvh, 200px)',
    overflowY: 'auto' as const,
  },
};

export const dashboardSteps: TourStep[] = [
  {
    target: '[data-tour="create-job"]',
    title: 'Your guided tour starts here',
    content:
      'Click Here to open the job creation stepper.',
    placement: 'bottom',
    skipBeacon: true,
    buttons: ['back', 'close', 'skip'],
  },
  {
    target: '[data-tour="job-stepper-modal"]',
    title: 'Job Creation Stepper',
    content:
      'This guided stepper creates your job offer in sequence: title, job description, CV upload, then AI analysis.',
    placement: 'right',
  },
  {
    target: '[data-tour="job-listings"]',
    title: 'Your Jobs Appear Here',
    content:
      'As jobs are created, they appear in this list. Use search and filters here to quickly find the right role, then click any row to open the full hiring pipeline.',
    placement: 'left',
  },
  {
    target: '[data-tour="job-card-actions"]',
    title: 'Quick Actions Per Job',
    content:
      'Each job row has quick actions like view details, add documents/CVs, and either archive active jobs or delete archived jobs.',
    placement: 'left',
  },
  {
    target: '[data-tour="usage-plan"]',
    title: 'Track Plan Usage in Real Time',
    content:
      'CV scoring and interview credits are tracked here so you can plan hiring volume confidently.',
    placement: 'left',
  },
  {
    target: '[data-tour="job-offer-card"]',
    title: 'Open the Job to Continue',
    content:
      'Click this job offer card to open Job Details and continue the guided tour.',
    placement: 'left',
    buttons: ['back', 'close', 'skip'],
  },
];

export const jobDetailsSteps: TourStep[] = [
  {
    target: () => firstJobDetailsTourAnchor(),
    title: 'Job Offer at a Glance',
    content:
      'All requirement fields are centralized here. Every incoming CV is scored against this job brief.',
    skipBeacon: true,
    placement: 'right',
  },
  {
    tourStepKey: 'pipeline-workspace',
    target: () => firstVisibleTourTarget('[data-tour="candidates-panel"]'),
    title: 'Candidate Pipeline Workspace',
    content:
    'Candidates are grouped by stage and ranked by match or assessment data. Switch stages with the tabs, use search and filters to narrow the list, and use the actions on each row by status. Click a row to open the Meta CV, which includes profile, fit read, and interview assessment when available.',
    placement: 'left',
  },
  {
    tourStepKey: 'meta-cv-from-table',
    target: () => firstVisibleTourTarget('[data-tour="meta-cv-close"]'),
    spotlightTarget: () => firstVisibleTourTarget('[data-tour="meta-cv-tour-panel"]'),
    data: { allowTooltipDrag: true } satisfies ProductTourStepData,
    title: 'Inside the Meta CV',
    content:
      'Here you get structured CV intelligence, match read, interview status or link, and assessment sections when they exist, plus downloads and candidate actions. Close the modal anytime from the X. Use Next when you are ready to continue the tour.',
    placement: 'bottom',
    skipBeacon: true,
    styles: metaCvTooltipStyles,
  },
  {
    tourStepKey: 'performance-toggle',
    target: () => firstVisibleTourTarget('[data-tour="candidates-performance-toggle"]'),
    title: 'Candidates performance chart',
    content:
      'Use the bar-chart control to open the performance view. You can compare candidates who have assessment data at a glance. Open it to continue.',
    placement: 'bottom',
    skipBeacon: true,
    spotlightClicks: true,
    spotlightPadding: 8,
    buttons: ['back', 'close', 'skip'],
  },
  {
    tourStepKey: 'performance-chart',
    target: () => firstVisibleTourTarget('[data-tour="candidates-performance-chart"]'),
    title: 'Candidates Performance Dashboard',
    content:
    "Soft skills are shown along the bottom, with the technical score on the side; dot colors match the legend. The shaded corner highlights strong scores in both areas. Click any dot to open the Meta CV in a centered modal, displaying the candidate’s profile and assessment when available.",
    skipBeacon: true,
  },
  {
    tourStepKey: 'meta-cv-from-chart',
    target: () => firstVisibleTourTarget('[data-tour="meta-cv-close"]'),
    spotlightTarget: () => firstVisibleTourTarget('[data-tour="meta-cv-tour-panel"]'),
    data: { allowTooltipDrag: true } satisfies ProductTourStepData,
    title: 'Inside the Meta CV',
    content:
      'Same Meta CV as from the table: profile and CV intelligence, match read, interview and assessment when available, and actions. Next returns you to the pipeline flow and closes this modal for you.',
    placement: 'bottom',
    skipBeacon: true,
    styles: metaCvTooltipStyles,
  },
  {
    tourStepKey: 'add-cv',
    target: () => firstVisibleTourTarget('[data-tour="add-cv-btn"]'),
    title: 'Add Candidates to the Pipeline',
    content:
      'Use Add CV to upload CVs directly and add candidates to this job’s pipeline.',
    placement: 'bottom',
    buttons: ['back', 'close', 'primary', 'skip'],
  },
  {
    target: () =>
      firstVisibleTourTarget('[data-tour="send-interview-btn"]'),
    title: 'Send AI Interviews in Bulk',
    content:
      'Select candidates in Pending and send interview invitations in one action. You will confirm recipients in a short dialog before they are sent.',
    placement: 'bottom',
  },
  {
    tourStepKey: 'more-actions-menu',
    target: () =>
      firstVisibleTourTarget('[data-tour="configure-exam-entry"]') ??
      firstVisibleTourTarget('[data-tour="exam-config-tab"]'),
    title: 'Open More Actions',
    content:
      'Open this menu to access advanced actions, including Public CV Upload and Configure Exam.',
    placement: 'bottom',
    skipBeacon: true,
    buttons: ['back', 'close', 'skip'],
  },
  {
    tourStepKey: 'public-cv-upload',
    target: () => firstVisibleTourTarget('[data-tour="public-cv-upload-entry"]'),
    title: 'Alternative Upload: Public CV Upload',
    content:
      'Use this option when you want a public/shareable CV upload channel instead of uploading directly from Add CV.',
    placement: 'left',
  },
  {
    tourStepKey: 'configure-exam-menu',
    target: () => firstVisibleTourTarget('[data-tour="configure-exam-menu-item"]'),
    title: 'Configure the Exam Before You Send',
    content: createElement(
      Fragment,
      null,
      createElement(
        'p',
        { className: 'm-0' },
        'Before anything goes out, open this menu and select Configure Exam. Your interview questions are already generated from your job offer, just review them, adjust what you need. You’re in control.',
      ),
      createElement(
        'p',
        { className: 'mt-3 mb-0 font-heading font-bold text-dark-invirtus' },
        'Go ahead and click it!',
      ),
    ),
    placement: 'left',
    skipBeacon: true,
    spotlightClicks: true,
    spotlightPadding: 8,
    buttons: ['back', 'close', 'skip'],
  },
  {
    tourStepKey: 'exam-config-overview',
    target: () => firstVisibleTourTarget('[data-tour="exam-config-page"]'),
    title: 'Your Interview, Fully in Your Hands',
    content:
      'Your exam has three sections: Technical Skills, Soft Skills, and Psychometric. Each one is pre-generated from your job offer. You can review every question, add new ones, modify or delete existing ones, and reorder them by dragging. If needed, switch the interview language between English and French using the toggle at the top. When you are done, hit Save. Your configuration is locked in and ready to go.',
    placement: 'bottom',
    skipBeacon: true,
  },
  {
    tourStepKey: 'exam-section-technical',
    target: () => firstVisibleTourTarget('[data-tour="exam-config-questions-technical"]'),
    title: 'A Closer Look at Each Question',
    content:
      'Inside each section, you have full control over every question. Change the time allocated, switch the format between Spoken, Code Review, or Code Write, edit the question itself, reorder by dragging, or delete it entirely. The exam is yours to shape.',
    placement: 'bottom',
    skipBeacon: true,
  },
  {
    tourStepKey: 'help-restart-tour',
    target: () => firstVisibleTourTarget('[data-tour="help-restart-tour"]'),
    title: 'You Can Always Come Back',
    content:
      'That is the full picture. Whenever you need a refresher, this button brings the tour back from the start.',
    placement: 'bottom',
  },
];

export function getJobDetailsTourStepIndexByKey(key: string): number {
  return jobDetailsSteps.findIndex((s) => (s as TourStep).tourStepKey === key);
}

/** Progress for Job Details tour: optional Meta CV steps do not increase total; current stays on the last main step until Next. */
export function getJobDetailsTourProgressDisplay(stepIndex: number): { current: number; total: number } {
  const total = Math.max(
    1,
    jobDetailsSteps.filter((s) => !JOB_DETAILS_OPTIONAL_PROGRESS_KEYS.has((s as TourStep).tourStepKey ?? '')).length,
  );

  const perIndex: number[] = [];
  let running = 0;
  for (let i = 0; i < jobDetailsSteps.length; i++) {
    const key = (jobDetailsSteps[i] as TourStep).tourStepKey ?? '';
    if (!JOB_DETAILS_OPTIONAL_PROGRESS_KEYS.has(key)) {
      running += 1;
    }
    perIndex[i] = running;
  }

  const clamped = Math.max(0, Math.min(stepIndex, perIndex.length - 1));
  return { current: perIndex[clamped] ?? 1, total };
}
